import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiBlob, apiRequest } from './client';
import type {
  CreatorProfile,
  GeneratedPrompt,
  GeneratedScript,
  InstagramAuthorization,
  InstagramConfirmation,
  InstagramConnection,
  InstagramLookup,
  Interest,
  MediaKit,
  Me,
  Pin,
  ProfileDraft,
  Prompt,
  PromptList,
  ScriptFormat,
  SocialAccount,
  Staleness
} from './types';

export const keys = {
  me: ['me'] as const,
  prompts: (query: string, category: string | null) => ['prompts', query, category] as const,
  prompt: (slug: string) => ['prompt', slug] as const,
  profile: ['profile'] as const,
  sessionAvatarKey: ['profile-avatar-key'] as const,
  profileAvatar: (objectKey: string) => ['profile-avatar', objectKey] as const,
  pins: (query: string, kind: string, order: string) => ['pins', query, kind, order] as const,
  pin: (id: string) => ['pin', id] as const,
  credits: ['credits'] as const,
  interest: ['interest'] as const,
  mediaKit: ['media-kit'] as const,
  instagramConnection: ['instagram-connection'] as const,
  socialAccounts: ['social-accounts'] as const
};

export function useMe(): UseQueryResult<Me> {
  return useQuery({ queryKey: keys.me, queryFn: () => apiRequest<Me>('/v1/me') });
}

export function usePrompts(query: string, category: string | null): UseQueryResult<PromptList> {
  const search = new URLSearchParams();
  if (query.trim() !== '') {
    search.set('q', query.trim());
  }
  if (category) {
    search.set('category', category);
  }
  const suffix = search.toString() === '' ? '' : `?${search}`;

  return useQuery({
    queryKey: keys.prompts(query, category),
    queryFn: () => apiRequest<PromptList>(`/v1/prompts${suffix}`),
    // Keeps the grid on screen while a new search resolves instead of blanking
    // the library between keystrokes.
    placeholderData: (previous) => previous
  });
}

export function usePrompt(slug: string): UseQueryResult<{ prompt: Prompt }> {
  return useQuery({
    queryKey: keys.prompt(slug),
    queryFn: () => apiRequest<{ prompt: Prompt }>(`/v1/prompts/${slug}`)
  });
}

export function useProfile(): UseQueryResult<{ profile: CreatorProfile | null }> {
  return useQuery({
    queryKey: keys.profile,
    queryFn: () => apiRequest<{ profile: CreatorProfile | null }>('/v1/profile')
  });
}

export function useSaveProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (profile: ProfileDraft) =>
      apiRequest<{ profile: CreatorProfile }>('/v1/profile', { method: 'PUT', body: profile }),
    onSuccess: (data) => {
      // Written straight into the cache: the summary card reads the profile
      // that was just saved, and a refetch here would blank it first.
      client.setQueryData(keys.profile, { profile: data.profile });
    }
  });
}

/**
 * `polling` is on while a refresh she asked for is still running. The work
 * happens off the request (ADR-0026), so the only way to know it finished is to
 * ask again — and the only way to keep the numbers on screen while asking is
 * `placeholderData`, or every poll blanks the cards for a frame.
 */
export function useMediaKit(polling = false): UseQueryResult<MediaKit> {
  return useQuery({
    queryKey: keys.mediaKit,
    queryFn: () => apiRequest<MediaKit>('/v1/media-kit'),
    placeholderData: (previous) => previous,
    refetchInterval: polling ? 3000 : false
  });
}

export function useRefreshMediaKit() {
  return useMutation({
    mutationFn: () =>
      apiRequest<{ status: string }>('/v1/media-kit/refresh', { method: 'POST' })
  });
}

/**
 * Whether her Instagram is connected, and whether she is even allowed to try.
 *
 * Polls while disconnected, because the flow finishes in a popup this window
 * cannot see into: the `postMessage` is the fast path and this is what covers
 * a popup that was closed by hand, or a browser that ate the message.
 */
export function useInstagramConnection(): UseQueryResult<InstagramConnection> {
  return useQuery({
    queryKey: keys.instagramConnection,
    queryFn: () => apiRequest<InstagramConnection>('/v1/connections/instagram'),
    retry: false,
    refetchInterval: (query) => (query.state.data?.connected === false ? 5000 : false)
  });
}

export function useAuthorizeInstagram() {
  return useMutation({
    mutationFn: () =>
      apiRequest<InstagramAuthorization>('/v1/connections/instagram/authorize', {
        method: 'POST'
      })
  });
}

export function useSaveSocialAccounts() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (accounts: Array<Pick<SocialAccount, 'network' | 'handle'>>) =>
      apiRequest<{ accounts: SocialAccount[] }>('/v1/social-accounts', {
        method: 'PUT',
        body: { accounts }
      }),
    onSuccess: (data) => {
      // The saved list written straight in, and the numbers left alone: a
      // network she just added has no snapshot yet, and refetching here would
      // blank the ones that do while the request is in flight.
      client.setQueryData<MediaKit>(keys.mediaKit, (current) => {
        const kept = (network: { network: string }) =>
          data.accounts.some((account) => account.network === network.network);
        return {
          accounts: data.accounts,
          networks: (current?.networks ?? []).filter(kept),
          summary: {
            reach: current?.summary.reach ?? { kind: 'audience', value: null, state: 'withheld' },
            networks: (current?.summary.networks ?? []).filter(kept)
          }
        };
      });
    }
  });
}

/** The most recent upload in this browser session, before or after the profile save. */
export function useSessionAvatarKey() {
  return useQuery({
    queryKey: keys.sessionAvatarKey,
    queryFn: async () => ({ thumbnailKey: null as string | null }),
    initialData: { thumbnailKey: null as string | null },
    staleTime: Infinity
  });
}

export function useProfileAvatar(objectKey: string | undefined) {
  return useQuery({
    queryKey: keys.profileAvatar(objectKey ?? 'unset'),
    queryFn: () => apiBlob(`/v1/profile/avatar/${objectKey!}`),
    enabled: Boolean(objectKey),
    retry: false,
    staleTime: Infinity
  });
}

export function useGeneratePrompt() {
  return useMutation({
    mutationFn: (input: { task: string; adjustments?: Record<string, string> }) =>
      apiRequest<GeneratedPrompt>('/v1/generator/prompts', { method: 'POST', body: input })
  });
}

export function usePins(query: string, kind: string, order: string): UseQueryResult<{ pins: Pin[] }> {
  const search = new URLSearchParams({ order });
  if (query.trim() !== '') search.set('q', query.trim());
  if (kind !== 'all') search.set('kind', kind);

  return useQuery({
    queryKey: keys.pins(query, kind, order),
    queryFn: () => apiRequest<{ pins: Pin[] }>(`/v1/pins?${search}`),
    // Keeps the list on screen while a new search resolves instead of blanking
    // it between keystrokes.
    placeholderData: (previous) => previous
  });
}

export function usePin(id: string): UseQueryResult<{ pin: Pin; staleness: Staleness }> {
  return useQuery({
    queryKey: keys.pin(id),
    queryFn: () => apiRequest<{ pin: Pin; staleness: Staleness }>(`/v1/pins/${id}`)
  });
}

export type CreatePinInput =
  | { kind: 'library'; sourceRef: string }
  | {
      kind: 'assembled';
      label: string;
      body: string;
      format: string;
      objective: string;
      adjustments?: { tone?: string; length?: 'short' | 'medium' | 'long'; platform?: string };
    }
  | { kind: 'script'; sourceRef: string; label: string; body: string; format: string };

export function useCreatePin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePinInput) => apiRequest<{ pin: Pin }>('/v1/pins', { method: 'POST', body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['pins'] });
      void client.invalidateQueries({ queryKey: keys.me });
    }
  });
}

export function useDeletePin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest(`/v1/pins/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['pins'] });
      void client.invalidateQueries({ queryKey: keys.me });
    }
  });
}

export function useCredits(): UseQueryResult<{ credits: { granted: number; used: number; balance: number } }> {
  return useQuery({
    queryKey: keys.credits,
    queryFn: () =>
      apiRequest<{ credits: { granted: number; used: number; balance: number } }>('/v1/scripts/credits')
  });
}

export function useGenerateScript() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { task: string; format: ScriptFormat }) =>
      apiRequest<GeneratedScript>('/v1/scripts', { method: 'POST', body: input }),
    onSuccess: (data) => {
      // The response already carries the debited balance, so the meter updates
      // without a second round trip.
      client.setQueryData(keys.credits, { credits: data.credits });
    }
  });
}

export function useInterest(): UseQueryResult<{ interest: Interest | null }> {
  return useQuery({
    queryKey: keys.interest,
    queryFn: () => apiRequest<{ interest: Interest | null }>('/v1/analysis-interest')
  });
}

export function useSaveInterest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { instagramHandle: string; question: string | null }) =>
      apiRequest<{ interest: Interest }>('/v1/analysis-interest', { method: 'POST', body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.interest });
    }
  });
}

/**
 * The bounded lookup of ADR-0020. Every non-`found` answer is a normal outcome
 * she can walk past by typing the numbers, so the caller reads `status` rather
 * than treating anything here as an error.
 */
export function useInstagramLookup() {
  return useMutation({
    mutationFn: (handle: string) =>
      apiRequest<InstagramLookup>(`/v1/instagram-profile?handle=${encodeURIComponent(handle)}`)
  });
}

/**
 * What "Sim, é esse" writes. The server does the three effects in one call, so
 * the only work here is telling every cache that already holds one of them.
 *
 * Every non-`found` status is a normal outcome the screen walks past, not an
 * error: the caller reads `status` and falls back to the manual confirmation.
 */
export function useConfirmInstagram() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (handle: string) =>
      apiRequest<InstagramConfirmation>('/v1/instagram-profile/confirm', {
        method: 'POST',
        body: { handle }
      }),
    onSuccess: (answer) => {
      if (answer.status !== 'found') {
        return;
      }

      if (answer.thumbnailKey) {
        // The only writer of these two caches since 2026-08-19, when the
        // by-hand upload was removed: the session slot is what the shell reads
        // before the profile is saved, and the blob has to be re-fetched under
        // the new key.
        client.setQueryData(keys.sessionAvatarKey, { thumbnailKey: answer.thumbnailKey });
        void client.invalidateQueries({ queryKey: keys.profileAvatar(answer.thumbnailKey) });
      }

      client.setQueryData<{ profile: CreatorProfile | null } | undefined>(
        keys.profile,
        (current) =>
          current?.profile
            ? {
                profile: {
                  ...current.profile,
                  identity: {
                    ...current.profile.identity,
                    followerCount: answer.profile.followers,
                    postCount: answer.profile.posts,
                    countsConfirmedAt: answer.profile.fetchedAt,
                    ...(answer.thumbnailKey
                      ? { instagramThumbnailKey: answer.thumbnailKey }
                      : {})
                  }
                }
              }
            : current
      );

      // The account and its first snapshot were written server-side, so
      // "Minhas redes" is stale the moment this returns.
      void client.invalidateQueries({ queryKey: keys.mediaKit });
    }
  });
}

export function useDeleteInterest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest('/v1/analysis-interest', { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.interest });
    }
  });
}

export type FeedbackTopic = 'blocked' | 'idea' | 'worked';

export function useSubmitFeedback() {
  return useMutation({
    mutationFn: (input: { topic: FeedbackTopic; message: string; surface?: string }) =>
      apiRequest<{ feedback: { id: string; topic: FeedbackTopic; createdAt: string } }>(
        '/v1/feedback',
        { method: 'POST', body: input }
      )
  });
}

export function requestSignInLink(email: string): Promise<unknown> {
  // Anonymous by necessity: asking for a link is what someone does when they
  // cannot sign in yet.
  return apiRequest('/v1/auth/sign-in-link', { method: 'POST', body: { email }, anonymous: true });
}
